import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cn } from '@/lib/cn';

/**
 * Overflow menu for the inspection header's secondary actions on narrow screens.
 *
 * This is the project's existing row-actions pattern — the one in
 * ReportTemplateLibraryPage, ProcedureLibraryPage and ProcedureBuilderPage —
 * with the same trigger, the same `fixed inset-0` click-catcher, the same
 * card/border/shadow menu and the same red treatment for destructive items. It
 * is lifted into a component rather than inlined a fourth time because this one
 * has to be reachable by keyboard, and that is more logic than belongs in the
 * middle of a 1600-line page.
 *
 * What it adds over the inline copies: `role="menu"`/`menuitem`, focus moved
 * into the menu on open and returned to the trigger on close, Escape to close,
 * and arrow/Home/End navigation. Everything else is deliberately identical, so
 * it still looks like the rest of the app.
 *
 * It renders nothing when no item is permitted — an empty menu is worse than no
 * menu, and the caller should not have to special-case that.
 */

export interface HeaderMenuItem {
  label: string;
  icon: IconDefinition;
  onSelect: () => void;
  /** Destructive actions keep the red treatment used elsewhere in the app. */
  danger?: boolean;
}

interface Props {
  items: HeaderMenuItem[];
  /** Accessible name for the trigger, e.g. "More actions for INS-00215". */
  label: string;
  className?: string;
}

export function HeaderActionsMenu({ items, label, className }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Move focus into the menu when it opens. Without this a keyboard user opens
  // the menu and their focus is still on the trigger, behind the overlay.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [open]);

  if (items.length === 0) return null;

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const focusables = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    );
    if (focusables.length === 0) return;
    const i = focusables.indexOf(document.activeElement as HTMLButtonElement);

    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusables[(i + 1) % focusables.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusables[(i - 1 + focusables.length) % focusables.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusables[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      focusables[focusables.length - 1]?.focus();
    } else if (e.key === 'Tab') {
      // Nothing behind the overlay is reachable, so leaving the menu means
      // closing it rather than tabbing into an inert page.
      close(true);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="inspection-header-actions-trigger"
      >
        <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => close(false)} aria-hidden />
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            onKeyDown={onMenuKeyDown}
            className="absolute right-0 top-12 z-20 min-w-[200px] rounded-xl border border-border bg-card py-1 shadow-lg"
            data-testid="inspection-header-actions-menu"
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  close(true);
                  item.onSelect();
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-4 py-3 text-sm transition-colors',
                  item.danger
                    ? 'text-red-600 hover:bg-red-500/10'
                    : 'text-foreground hover:bg-muted',
                )}
                data-testid={`inspection-header-action-${item.label
                  .replace(/\s+/g, '-')
                  .toLowerCase()}`}
              >
                <FontAwesomeIcon icon={item.icon} className="h-3.5 w-3.5 shrink-0" />
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
